import React, { useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/header";
import BlogCard from "../../components/BlogCard";
import BlogCover from "../../components/BlogCover";
import CTA from "../home/cta";
import useBlogStore from "../../store/blogStore";
import blogPlaceholder from "../../images/blog_placeholder.png";

const ITEMS_PER_PAGE = 6;

const instrumentSerif = { fontFamily: "'Instrument Serif', Georgia, serif" };
const geistMono = { fontFamily: "'Geist Mono', monospace" };
const geist = { fontFamily: "'Geist', sans-serif" };

const ACCENT = "#295B4A";
const DARK = "#132A22";
const CREAM = "#F7F4EE";
const DIVIDER = "rgba(19, 42, 34, 0.10)";

/* ---------------- content helpers ---------------- */
const getOrdinalSuffix = (n) => {
  if (n > 3 && n < 21) return "th";
  switch (n % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
};

const formatBlogDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const day = date.getDate();
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `Posted ${day}${getOrdinalSuffix(day)} ${months[date.getMonth()]}, ${date.getFullYear()}`;
};

const extractImageFromMarkdown = (text) => {
  if (!text) return null;
  const m = text.match(/!\[([^\]]*)\]\(([^)]+)\)/);
  return m ? m[2] : null;
};

const stripMarkdown = (text) => {
  if (!text) return "";
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/https?:\/\/[^\s]+/g, "")
    .replace(/[#*<>[\]()!\n]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const createSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const estimateReadTime = (content) => {
  if (!content) return "3 min read";
  const words = stripMarkdown(content).split(/\s+/).length;
  const mins = Math.max(1, Math.round(words / 200));
  return `${mins} min read`;
};

const coverFor = (blog) =>
  blog.header_image_url ||
  extractImageFromMarkdown(blog.blog_content) ||
  blogPlaceholder;

const blogHref = (blog) => `/blog/${blog.id}/${createSlug(blog.blog_title)}`;

/* ---------------- small presentational pieces ---------------- */
const SectionLabel = ({ children, color = ACCENT }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color, flexShrink: 0 }} />
    <span
      style={{
        ...geistMono,
        fontWeight: 500,
        fontSize: "12px",
        letterSpacing: "0.08em",
        color,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  </div>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
    <circle cx="11" cy="11" r="7" stroke="#5E5E5E" strokeWidth="1.6" />
    <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="#5E5E5E" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/* Large featured article (left of the rail) */
const FeaturedArticle = ({ blog }) => {
  const navigate = useNavigate();
  const excerpt = stripMarkdown(blog.blog_content).slice(0, 180);
  const meta = [formatBlogDate(blog.created_at), estimateReadTime(blog.blog_content)]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="blog-featured-main"
      onClick={() => navigate(blogHref(blog))}
      style={{ cursor: "pointer", display: "flex", flexDirection: "column" }}
    >
      <BlogCover
        src={coverFor(blog)}
        alt={blog.blog_title}
        onError={(e) => { e.target.src = blogPlaceholder; }}
        imgClassName="blog-featured-img"
        style={{ width: "100%", height: "clamp(260px, 30vw, 420px)" }}
        imgStyle={{ transition: "transform 0.4s ease" }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "14px", padding: "clamp(20px, 1.9vw, 30px)" }}>
        <p style={{ ...geist, fontWeight: 400, fontSize: "clamp(12px, 0.94vw, 13px)", color: "#5E5E5E", margin: 0 }}>
          {meta}
        </p>
        <h3
          style={{
            ...geist,
            fontWeight: 600,
            fontSize: "clamp(20px, 1.94vw, 28px)",
            lineHeight: 1.3,
            color: DARK,
            margin: 0,
          }}
        >
          {blog.blog_title}
        </h3>
        <p style={{ ...geist, fontWeight: 400, fontSize: "clamp(14px, 1.04vw, 16px)", lineHeight: 1.7, color: "#444B47", margin: 0 }}>
          {excerpt}
          {excerpt.length >= 180 ? "… " : " "}
          <span className="blog-read-more" style={{ color: "#3F5445", textDecoration: "underline", fontWeight: 500 }}>
            Read more
          </span>
        </p>
      </div>
    </div>
  );
};

/* Compact article row (right side list) */
const SideArticle = ({ blog }) => {
  const navigate = useNavigate();
  return (
    <div
      className="blog-side-item"
      onClick={() => navigate(blogHref(blog))}
      style={{ display: "flex", gap: "16px", cursor: "pointer", alignItems: "flex-start" }}
    >
      <BlogCover
        src={coverFor(blog)}
        alt={blog.blog_title}
        onError={(e) => { e.target.src = blogPlaceholder; }}
        style={{
          width: "clamp(88px, 8.5vw, 116px)",
          height: "clamp(76px, 7vw, 96px)",
          borderRadius: "10px",
          flexShrink: 0,
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }}>
        <p style={{ ...geist, fontWeight: 400, fontSize: "clamp(11px, 0.83vw, 12px)", color: "#5E5E5E", margin: 0 }}>
          {formatBlogDate(blog.created_at)}
        </p>
        <p
          style={{
            ...geist,
            fontWeight: 500,
            fontSize: "clamp(14px, 1.04vw, 16px)",
            lineHeight: 1.4,
            color: DARK,
            margin: 0,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {blog.blog_title}
        </p>
        <span className="blog-read-more" style={{ ...geist, fontSize: "clamp(12px, 0.9vw, 13px)", fontWeight: 500, color: "#3F5445" }}>
          Read more →
        </span>
      </div>
    </div>
  );
};

/* Pagination matching the design (← 1 2 3 →) */
const Pagination = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  const arrowStyle = (disabled) => ({
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: `1px solid ${DIVIDER}`,
    background: "transparent",
    color: disabled ? "#B7BDB8" : DARK,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
  });

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "clamp(40px, 4.5vw, 64px)" }}>
      <button aria-label="Previous page" style={arrowStyle(page <= 1)} disabled={page <= 1} onClick={() => onChange(page - 1)}>
        ←
      </button>
      {pages.map((p) => {
        const active = p === page;
        return (
          <button
            key={p}
            onClick={() => onChange(p)}
            style={{
              minWidth: "40px",
              height: "40px",
              padding: "0 6px",
              borderRadius: "8px",
              border: active ? `1px solid ${ACCENT}` : "1px solid transparent",
              background: active ? "rgba(41, 91, 74, 0.06)" : "transparent",
              color: active ? ACCENT : "#5E5E5E",
              fontWeight: active ? 600 : 500,
              cursor: "pointer",
              ...geist,
              fontSize: "15px",
            }}
          >
            {p}
          </button>
        );
      })}
      <button aria-label="Next page" style={arrowStyle(page >= totalPages)} disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        →
      </button>
    </div>
  );
};

const SkeletonBlock = ({ height = "240px", radius = "16px" }) => (
  <div
    style={{
      width: "100%",
      height,
      backgroundColor: "#ECEAE2",
      borderRadius: radius,
      animation: "blogPulse 1.5s ease-in-out infinite",
    }}
  />
);

/* ---------------- page ---------------- */
/* Hero heading that auto-sizes its font so it always renders as exactly two
   non-wrapping lines ("Blog insights to help you grow" / "and manage your money.")
   that fit the available width on any screen — measured in JS so it does not
   depend on container-query support. */
const FitBlogHeading = React.memo(() => {
  const wrapRef = useRef(null);
  const headingRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const heading = headingRef.current;
    if (!wrap || !heading) return undefined;

    const REF_SIZE = 100; // reference size used to measure the text's intrinsic width
    const MIN = 18;
    const MAX = 64;
    const SAFETY = 0.98; // leave a hair of margin so nothing ever overflows

    // Ratio of font-size to the widest line's width. It is constant for this
    // (fixed) text + font, so we measure it once instead of on every resize —
    // that keeps the resize handler from mutating what it observes.
    let ratio = 0;
    let lastWidth = -1;
    let rafId = 0;

    const measureRatio = () => {
      const previous = heading.style.fontSize;
      heading.style.fontSize = `${REF_SIZE}px`;
      const widestLine = heading.scrollWidth; // widest of the two <br>-separated lines
      heading.style.fontSize = previous;
      if (widestLine) ratio = REF_SIZE / widestLine;
    };

    const apply = () => {
      const available = wrap.clientWidth;
      if (!available || !ratio || available === lastWidth) return;
      lastWidth = available;
      const size = Math.max(MIN, Math.min(MAX, available * SAFETY * ratio));
      heading.style.fontSize = `${size}px`;
    };

    measureRatio();
    apply();

    // Defer to the next frame so the font-size change never happens synchronously
    // inside the observer callback (avoids the benign "ResizeObserver loop" warning).
    const observer = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(apply);
    });
    observer.observe(wrap);

    // The serif loads asynchronously; re-measure with its real metrics once ready.
    let active = true;
    if (typeof document !== "undefined" && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (!active) return;
        measureRatio();
        lastWidth = -1; // force re-apply with the updated ratio
        apply();
      });
    }

    return () => {
      active = false;
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={wrapRef} style={{ width: "100%" }}>
      <h1
        ref={headingRef}
        style={{
          ...instrumentSerif,
          fontWeight: 400,
          whiteSpace: "nowrap",
          lineHeight: 1.08,
          letterSpacing: "-0.01em",
          color: DARK,
          margin: 0,
        }}
      >
        Blog insights to help you grow<br />
        and <em style={{ fontStyle: "italic", color: ACCENT }}>manage</em> your money.
      </h1>
    </div>
  );
});

const Main = () => {
  const {
    blogs,
    featuredBlogs,
    totalCount,
    currentPage,
    searchQuery,
    loading,
    error,
    fetchBlogs,
    setSearchQuery,
    setCurrentPage,
  } = useBlogStore();

  useEffect(() => {
    fetchBlogs(currentPage, ITEMS_PER_PAGE);
  }, [currentPage, fetchBlogs]);

  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));

  const query = searchQuery.trim().toLowerCase();
  const isSearching = query.length > 0;
  const gridBlogs = isSearching
    ? blogs.filter(
        (b) =>
          (b.blog_title || "").toLowerCase().includes(query) ||
          stripMarkdown(b.blog_content).toLowerCase().includes(query)
      )
    : blogs;

  const featured = featuredBlogs.length ? featuredBlogs : blogs.slice(0, 4);
  const heroBlog = featured[0];
  const sideBlogs = featured.slice(1, 4);

  const initialLoading = loading && blogs.length === 0;

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <style>{`
        @keyframes blogPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }

        .blog-featured-main {
          background-color: #FFFFFF;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid rgba(19, 42, 34, 0.05);
          box-shadow: 0 1px 2px rgba(19, 42, 34, 0.04), 0 18px 40px -22px rgba(19, 42, 34, 0.18);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .blog-featured-main:hover {
          transform: translateY(-2px);
          box-shadow: 0 1px 2px rgba(19, 42, 34, 0.04), 0 26px 52px -22px rgba(19, 42, 34, 0.26);
        }

        .blog-grid-card .blog-grid-card-img,
        .blog-featured-main .blog-featured-img { will-change: transform; }
        .blog-grid-card:hover .blog-grid-card-img,
        .blog-featured-main:hover .blog-featured-img { transform: scale(1.04); }
        .blog-grid-card:hover .blog-read-more,
        .blog-featured-main:hover .blog-read-more,
        .blog-side-item:hover .blog-read-more { color: #295B4A; }

        .blog-side-item:not(:last-child) {
          padding-bottom: clamp(18px, 1.8vw, 26px);
          border-bottom: 1px solid ${DIVIDER};
        }

        .blog-search-input:focus-within { border-color: ${ACCENT} !important; }

        .blog-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: clamp(20px, 2.2vw, 32px) clamp(20px, 1.7vw, 24px);
        }

        @media (max-width: 1024px) {
          .blog-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 900px) {
          .blog-hero-row { flex-direction: column !important; align-items: stretch !important; }
          .blog-hero-search { width: 100% !important; align-items: flex-start !important; }
          .blog-search-input { width: 100% !important; max-width: 520px; }
          /* On mobile the whole featured rail (big card + 3 side blogs) is dropped;
             the latest posts flow into the single-column grid below. */
          .blog-featured-row { display: none !important; }
          .blog-all-articles-heading { display: none !important; }
        }
        @media (max-width: 600px) {
          .blog-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* ---------- HERO + FEATURED + ALL ARTICLES share one cream→white gradient
           that resolves to white around the middle of the All Articles section ---------- */}
      <div style={{ background: `linear-gradient(180deg, ${CREAM} 0%, #FFFFFF 80%)` }}>
      <section
        style={{
          boxSizing: "border-box",
          padding: "clamp(28px, 3vw, 48px) clamp(20px, 5.56vw, 80px) clamp(48px, 6vw, 88px)",
        }}
      >
        <Header />

        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          {/* Hero */}
          <div
            className="blog-hero-row"
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: "clamp(24px, 3vw, 56px)",
              marginTop: "clamp(20px, 2.5vw, 40px)",
              marginBottom: "clamp(40px, 5vw, 72px)",
            }}
          >
            {/* Left: label + heading + subtitle */}
            <div style={{ flex: "1 1 0", maxWidth: "660px", minWidth: 0, display: "flex", flexDirection: "column", gap: "clamp(16px, 1.7vw, 24px)" }}>
              <SectionLabel>Zikora Blog</SectionLabel>
              <FitBlogHeading />
              <p style={{ ...geist, fontWeight: 400, fontSize: "clamp(14px, 1.18vw, 17px)", lineHeight: 1.65, color: "#5E5E5E", margin: 0, maxWidth: "520px" }}>
                Explore practical tips, business advice, and updates from Zikora to help you make smarter financial decisions every day.
              </p>
            </div>

            {/* Right: search */}
            <div
              className="blog-hero-search"
              style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "flex-start", flexShrink: 0 }}
            >
              <span style={{ ...geistMono, fontSize: "12px", letterSpacing: "0.08em", textTransform: "uppercase", color: "#5E5E5E" }}>
                Search the Zikora blog
              </span>
              <div
                className="blog-search-input"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  width: "clamp(280px, 28vw, 360px)",
                  height: "48px",
                  padding: "0 16px",
                  background: "#FFFFFF",
                  border: "1px solid rgba(19, 42, 34, 0.14)",
                  borderRadius: "10px",
                  transition: "border-color 0.2s ease",
                }}
              >
                <SearchIcon />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search articles, topics, or keywords"
                  style={{
                    ...geist,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: DARK,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Featured rail — hidden while actively searching */}
          {!isSearching && (
            initialLoading ? (
              <div className="blog-featured-row" style={{ display: "flex", gap: "clamp(24px, 2.8vw, 40px)" }}>
                <div style={{ flex: "1.55 1 0", minWidth: 0 }}>
                  <SkeletonBlock height="clamp(260px, 30vw, 420px)" />
                </div>
                <div className="blog-featured-side" style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: "20px" }}>
                  {[0, 1, 2].map((i) => <SkeletonBlock key={i} height="90px" radius="10px" />)}
                </div>
              </div>
            ) : heroBlog ? (
              <div className="blog-featured-row" style={{ display: "flex", gap: "clamp(24px, 2.8vw, 40px)", alignItems: "flex-start" }}>
                <div style={{ flex: "1.55 1 0", minWidth: 0 }}>
                  <FeaturedArticle blog={heroBlog} />
                </div>
                <div className="blog-featured-side" style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", gap: "clamp(18px, 1.8vw, 26px)" }}>
                  {sideBlogs.map((blog) => (
                    <SideArticle key={blog.id} blog={blog} />
                  ))}
                </div>
              </div>
            ) : null
          )}
        </div>
      </section>

      {/* ---------- ALL ARTICLES (sits on the shared gradient) ---------- */}
      <section
        style={{
          boxSizing: "border-box",
          padding: "clamp(48px, 6vw, 88px) clamp(20px, 5.56vw, 80px)",
        }}
      >
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <h2
            className={isSearching ? undefined : "blog-all-articles-heading"}
            style={{
              ...instrumentSerif,
              fontWeight: 400,
              fontSize: "clamp(28px, 3.3vw, 48px)",
              lineHeight: 1.1,
              color: DARK,
              margin: "0 0 clamp(28px, 3vw, 44px)",
            }}
          >
            {isSearching ? `Search results for “${searchQuery.trim()}”` : "All Articles"}
          </h2>

          {error ? (
            <div style={{ textAlign: "center", padding: "48px 0" }}>
              <p style={{ ...geist, color: "#B23B3B", fontSize: "16px", margin: "0 0 8px" }}>{error}</p>
              <button
                onClick={() => fetchBlogs(currentPage, ITEMS_PER_PAGE)}
                style={{ ...geist, marginTop: "12px", padding: "10px 20px", borderRadius: "8px", border: `1px solid ${ACCENT}`, background: "transparent", color: ACCENT, cursor: "pointer", fontWeight: 500 }}
              >
                Try again
              </button>
            </div>
          ) : initialLoading ? (
            <div className="blog-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <SkeletonBlock height="clamp(180px, 17vw, 240px)" />
                  <SkeletonBlock height="14px" radius="6px" />
                  <SkeletonBlock height="20px" radius="6px" />
                </div>
              ))}
            </div>
          ) : gridBlogs.length === 0 ? (
            <p style={{ ...geist, textAlign: "center", color: "#5E5E5E", fontSize: "16px", padding: "48px 0" }}>
              {isSearching ? "No articles match your search." : "No articles yet — check back soon."}
            </p>
          ) : (
            <>
              <div className="blog-grid" style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s ease" }}>
                {gridBlogs.map((blog) => (
                  <BlogCard key={blog.id} blog={blog} />
                ))}
              </div>
              {!isSearching && (
                <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
              )}
            </>
          )}
        </div>
      </section>
      </div>

      {/* ---------- CTA ---------- */}
      <CTA />
    </>
  );
};

export default Main;
